import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, Moon, Sun } from 'lucide-react'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { DialogTitle } from '@/components/ui/dialog'
import { apiClient } from '@/api/client'
import { useTheme } from '@/theme/ThemeProvider'
import { visibleNav } from './SidebarNav'
import type { Role } from '@/api/types'

interface Props {
  role: Role | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CommandPalette({ role, open, onOpenChange }: Props) {
  const navigate = useNavigate()
  const { mode, setMode } = useTheme()

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        onOpenChange(!open)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onOpenChange])

  const run = (fn: () => void) => () => {
    onOpenChange(false)
    fn()
  }

  const logout = async () => {
    try {
      const { redirect_url } = await apiClient.logout()
      window.location.href = redirect_url
    } catch {
      window.location.href = '/api/auth/login'
    }
  }

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <DialogTitle className="sr-only">Command palette</DialogTitle>
      <CommandInput placeholder="Go to a page, toggle theme, sign out…" />
      <CommandList>
        <CommandEmpty>No results.</CommandEmpty>
        <CommandGroup heading="Navigate">
          {visibleNav(role).map((item) => {
            const Icon = item.icon
            return (
              <CommandItem
                key={item.to}
                value={`${item.label} ${item.to}`}
                onSelect={run(() => navigate(item.to))}
              >
                <Icon className="mr-2 h-4 w-4" aria-hidden="true" />
                {item.label}
              </CommandItem>
            )
          })}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Actions">
          <CommandItem
            value="toggle theme light dark mode"
            onSelect={run(() => setMode(mode === 'dark' ? 'light' : 'dark'))}
          >
            {mode === 'dark' ? (
              <Sun className="mr-2 h-4 w-4" aria-hidden="true" />
            ) : (
              <Moon className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          </CommandItem>
          <CommandItem value="log out sign out" onSelect={run(logout)}>
            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
            Log out
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}
